import { c as _c } from "react/compiler-runtime";
import { useApi } from "@/hooks/useApi";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useActiveGithubOrgForUser } from "./useConnectedGithubOrgs";
import * as z from "zod";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useRef } from "react";

// Schema for runner region response
const runnerRegionSchema = z.object({
  region: z.enum(["eu-central", "us-west", "eu-west", "us-central", "us-east"]),
  supports_sticky_disks: z.boolean().optional()
});
type RunnerRegionResponse = z.infer<typeof runnerRegionSchema>;

/**
 * Hook to manage Runner Region setting
 * This setting controls where runner VMs are provisioned
 *
 * Pass `{ suspense: false }` when the calling component is not rendered
 * under a Suspense boundary (the query then reports `isLoading` instead of
 * suspending, and errors are surfaced via missing data rather than thrown
 * to an error boundary).
 *
 * Pass `{ org }` to read another organization's region instead of the
 * active org's (e.g. internal monitoring rules that target a customer org).
 */
export function useRunnerRegion(options) {
  const $ = _c(23);
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const queryClient = useQueryClient();
  const toastShownRef = useRef(false);
  const org = options?.org ?? activeGithubOrg;
  const suspense = options?.suspense ?? true;
  let t0;
  if ($[0] !== org) {
    t0 = ["runnerRegion", org];
    $[0] = org;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== api || $[3] !== org) {
    t1 = async () => {
      ;
      try {
        const response = await api.get(`/user/github/orgs/${org}/runner-region`);
        const parsed = runnerRegionSchema.parse(response.data);
        return parsed;
      } catch (t2) {
        const error = t2;
        console.error("Error fetching runner region:", error);
        throw error;
      }
    };
    $[2] = api;
    $[3] = org;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const t2 = !!org;
  const t3 = suspense ? _temp : false;
  let t4;
  if ($[5] !== suspense || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      enabled: t2,
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: 1,
      suspense,
      useErrorBoundary: t3
    };
    $[5] = suspense;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data,
    isLoading
  } = useQuery(t0, t1, t4);
  let t5;
  if ($[9] !== api || $[10] !== org) {
    t5 = async region => {
      const response_0 = await api.put(`/user/github/orgs/${org}/runner-region`, {
        region
      });
      return {
        response: response_0,
        region
      };
    };
    $[9] = api;
    $[10] = org;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== org || $[13] !== queryClient) {
    t6 = {
      onSuccess: data_0 => {
        queryClient.invalidateQueries(["runnerRegion", org]);
        if (!toastShownRef.current) {
          toastShownRef.current = true;
          setTimeout(() => {
            toastShownRef.current = false;
          }, 1000);
          toast.success(`Runner region updated to ${data_0.region.toUpperCase()} successfully`);
        }
      },
      onError: _temp2
    };
    $[12] = org;
    $[13] = queryClient;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const mutation = useMutation(t5, t6);
  const t7 = data?.region ?? "us-west";
  const t8 = data !== undefined;
  const t9 = data?.supports_sticky_disks ?? true;
  let t10;
  if ($[15] !== isLoading || $[16] !== mutation.error || $[17] !== mutation.isLoading || $[18] !== mutation.mutate || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = {
      region: t7,
      regionResolved: t8,
      supportsStickyDisks: t9,
      isLoading,
      updateRegion: mutation.mutate,
      isUpdating: mutation.isLoading,
      error: mutation.error
    };
    $[15] = isLoading;
    $[16] = mutation.error;
    $[17] = mutation.isLoading;
    $[18] = mutation.mutate;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp2(error_1) {
  console.error("Error updating runner region:", error_1);
  toast.error("Failed to update runner region. Please try again.");
  if (error_1 instanceof AxiosError) {
    console.error("Response status:", error_1.response?.status);
    console.error("Response data:", error_1.response?.data);
  }
}
function _temp(error_0) {
  return error_0?.response?.status !== 404;
}